import moment from 'moment';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { serverURL } from 'config';
import { encodeGedId, getUserIdFromToken } from 'utils';

const sanitizeFileName = (value) => String(value || '')
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, '_');

export const buildApaveDocumentUrl = (ged, profile, accessToken) => {
    if (!ged || !accessToken) {
        return '';
    }

    const encodedGed = encodeGedId(ged);
    let userId = profile?.id || '';
    if (!userId) {
        userId = getUserIdFromToken(accessToken);
    }

    const encodedUserId = btoa(unescape(encodeURIComponent(userId)));
    const encodedAccessToken = btoa(unescape(encodeURIComponent(accessToken)));

    return `${serverURL}assets/${encodeURIComponent(encodedGed)}/${encodeURIComponent(encodedUserId)}/${encodeURIComponent(encodedAccessToken)}`;
};

export const getDocumentDownloadUrl = (doc, profile, accessToken) => {
    if (doc?.ged) {
        return buildApaveDocumentUrl(doc.ged, profile, accessToken);
    }

    if (doc?.link) {
        return `${serverURL}${doc.link}`;
    }

    return '';
};

export const getZipEntryName = (doc, index) => {
    const typePart = doc.type ? `${sanitizeFileName(doc.type)}_` : '';
    const datePart = doc.documentDate
        ? `${moment(doc.documentDate).format('DDMMYYYY')}_`
        : '';
    const titlePart = sanitizeFileName(doc.title || doc.id || `document_${index + 1}`);
    return `${typePart}${datePart}${titlePart}.pdf`;
};

const ensureUniqueZipName = (name, usedNames) => {
    if (!usedNames.has(name)) {
        usedNames.add(name);
        return name;
    }

    const extension = '.pdf';
    const baseName = name.endsWith(extension) ? name.slice(0, -extension.length) : name;
    let counter = 1;
    let candidate = `${baseName}_${counter}${extension}`;

    while (usedNames.has(candidate)) {
        counter += 1;
        candidate = `${baseName}_${counter}${extension}`;
    }

    usedNames.add(candidate);
    return candidate;
};

const isPdfBuffer = (buffer) => {
    if (!buffer || buffer.byteLength < 4) {
        return false;
    }
    const header = new TextDecoder().decode(buffer.slice(0, 4));
    return header === '%PDF';
};

const fetchDocumentPdf = async (url, authorization) => {
    const response = await fetch(url, {
        method: 'GET',
        headers: authorization ? { Authorization: authorization } : {},
    });

    if (!response.ok) {
        throw new Error(`HTTP_${response.status}`);
    }

    const buffer = await response.arrayBuffer();

    if (!isPdfBuffer(buffer)) {
        throw new Error('INVALID_PDF');
    }

    return buffer;
};

export const openPdfBlobInViewer = (blob, displayUrl = '') => {
    if (!blob || blob.size === 0) {
        throw new Error('EMPTY_BLOB');
    }

    const blobUrl = window.URL.createObjectURL(blob);

    if (displayUrl) {
        window.history.pushState({}, '', displayUrl);
    }

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.zIndex = '9999';
    container.style.backgroundColor = '#fff';

    const embed = document.createElement('embed');
    embed.style.width = '100%';
    embed.style.height = '100%';
    embed.style.border = 'none';
    embed.src = blobUrl;
    embed.type = 'application/pdf';

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.src = blobUrl;
    iframe.style.display = 'none';

    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '×';
    closeBtn.style.position = 'fixed';
    closeBtn.style.top = '10px';
    closeBtn.style.right = '10px';
    closeBtn.style.zIndex = '10000';
    closeBtn.style.width = '40px';
    closeBtn.style.height = '40px';
    closeBtn.style.borderRadius = '50%';
    closeBtn.style.border = 'none';
    closeBtn.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    closeBtn.style.color = 'white';
    closeBtn.style.fontSize = '24px';
    closeBtn.style.cursor = 'pointer';
    closeBtn.style.display = 'flex';
    closeBtn.style.alignItems = 'center';
    closeBtn.style.justifyContent = 'center';

    const cleanup = () => {
        if (container.parentNode) {
            document.body.removeChild(container);
        }
        if (closeBtn.parentNode) {
            document.body.removeChild(closeBtn);
        }
        window.URL.revokeObjectURL(blobUrl);
    };

    embed.onerror = () => {
        embed.style.display = 'none';
        iframe.style.display = 'block';
    };

    iframe.onerror = cleanup;

    closeBtn.onclick = cleanup;

    container.appendChild(embed);
    container.appendChild(iframe);
    document.body.appendChild(container);
    document.body.appendChild(closeBtn);
    window.addEventListener('popstate', cleanup, { once: true });
};

export const previewSingleDocument = async ({
    doc,
    profile,
    accessToken,
    tokenType = 'Bearer',
}) => {
    if (doc?.ged) {
        const url = getDocumentDownloadUrl(doc, profile, accessToken);
        if (!url) {
            throw new Error('NO_URL');
        }

        const authorization = accessToken ? `${tokenType} ${accessToken}` : '';
        const buffer = await fetchDocumentPdf(url, authorization);
        const blob = new Blob([buffer], { type: 'application/pdf' });
        openPdfBlobInViewer(blob, url);
        return;
    }

    if (doc?.link) {
        const link = doc.link.startsWith('http') ? doc.link : `${serverURL}${doc.link}`;
        window.open(link, '_blank');
        return;
    }

    throw new Error('NO_URL');
};

export const downloadSingleDocument = async ({
    doc,
    profile,
    accessToken,
    tokenType = 'Bearer',
}) => {
    const url = getDocumentDownloadUrl(doc, profile, accessToken);
    if (!url) {
        throw new Error('NO_URL');
    }

    const authorization = accessToken ? `${tokenType} ${accessToken}` : '';
    const buffer = await fetchDocumentPdf(url, authorization);
    const filename = doc.ged ? `${doc.title}.pdf` : (doc.title || 'document.pdf');
    const blob = new Blob([buffer], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);

    if (window.dataLayer) {
        window.dataLayer.push({
            event: 'download',
            file_name: filename,
            file_type: 'PDF',
        });
    }

    return filename;
};

export const downloadAllDocumentsZip = async (
    documents,
    {
        profile,
        accessToken,
        tokenType = 'Bearer',
        zipName = 'documents.zip',
        onProgress,
    } = {},
) => {
    const downloadableDocuments = (documents || []).filter((doc) => doc.ged || doc.link);
    const total = downloadableDocuments.length;

    if (!total) {
        return { added: 0, failed: 0, total: 0, zipGenerated: false };
    }

    const zip = new JSZip();
    const usedNames = new Set();
    let added = 0;
    let failed = 0;
    const authorization = accessToken ? `${tokenType} ${accessToken}` : '';

    for (let i = 0; i < downloadableDocuments.length; i += 1) {
        const doc = downloadableDocuments[i];

        if (onProgress) {
            onProgress({
                current: i + 1,
                total,
                documentTitle: doc.title || '',
                phase: 'downloading',
            });
        }

        const url = getDocumentDownloadUrl(doc, profile, accessToken);

        if (!url) {
            failed += 1;
            continue;
        }

        try {
            const buffer = await fetchDocumentPdf(url, authorization);
            const entryName = ensureUniqueZipName(getZipEntryName(doc, i), usedNames);
            zip.file(entryName, buffer);
            added += 1;
        } catch (err) {
            console.log(err);
            failed += 1;
        }
    }

    if (added === 0) {
        return { added: 0, failed, total, zipGenerated: false };
    }

    if (onProgress) {
        onProgress({
            current: total,
            total,
            documentTitle: '',
            phase: 'zipping',
        });
    }

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, zipName);

    return { added, failed, total, zipGenerated: true };
};
